import Widget from "@arcgis/core/widgets/Widget";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import PortalItem from "@arcgis/core/portal/PortalItem";
import OAuthInfo from "@arcgis/core/identity/OAuthInfo";
import config from "@arcgis/core/config";

import PortalUser from "./utils/Portal/PortalUser";
import InstantAppLanding from "./ui/InstantAppLanding";

import appConfigType from "../config/appConfig.json";
import Portal from "./utils/Portal/Portal";

// esri-loader override
import "./utils/require";

import AppT9N from "../t9n/App/App.json";
import { when } from "@arcgis/core/core/reactiveUtils";

import { Telemetry } from "@esri/telemetry";
import { Amazon } from "@esri/telemetry-amazon";
import { substitute } from "@arcgis/core/intl";
import { decideAndSetDir, decideAndSetLocale } from "./utils/t9nUtils";
import { isEnterprise } from "./utils/appUtils";
import PopoverManager from "./widgets/PopoverOnboarding/PopoverManager";

@subclass("Landing.App")
export default class App extends Widget {
  @property()
  appConfig: typeof appConfigType;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  i18nNoAppAccess: typeof AppT9N;

  @property()
  _isUserNotAllowed: boolean = false;

  private _calciteCSSTheme: "calcite-mode-dark" | "calcite-mode-light";

  private popoverManager: PopoverManager;

  constructor(args) {
    super(args);

    config.assetsPath = "assets";

    when(
      () => this?.appConfig !== undefined,
      () => {
        if (
          this.appConfig.portalUrl == null ||
          this.appConfig.portalUrl === ""
        ) {
          const portalUrl = this._getEsriEnvironmentPortalUrl();
          this.appConfig.portalUrl = portalUrl;
        }
        config.portalUrl = this.appConfig.portalUrl;
        if (window.location.href.indexOf("arcgis") !== -1) {
          this._registerOAuthInfos();
        }
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  postInitialize() {
    this.init();

    // initial call to setup dark mode
    this.__handleCalciteCSSThemeAssignment(
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
    // listener setup
    this._setCalciteCSSThemeListener();
  }

  render() {
    return this._isUserNotAllowed ? (
      <div class="not-allowed">
        {this.i18nNoAppAccess.noAppAccess.noCreatePrivileges}
      </div>
    ) : (
      <div id="instant-app-landing">{this?.popoverManager?.render()}</div>
    );
  }

  private _setCalciteCSSThemeListener(): void {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (e) =>
        this.__handleCalciteCSSThemeAssignment(e.matches)
      );
  }

  private __handleCalciteCSSThemeAssignment(isDarkMode: boolean): void {
    this._calciteCSSTheme = isDarkMode
      ? "calcite-mode-dark"
      : "calcite-mode-light";
    document.body.classList.remove(
      ...["calcite-mode-dark", "calcite-mode-light"]
    );
    document.body.classList.add(this._calciteCSSTheme);
    this.scheduleRender();
  }

  private _getEsriEnvironmentPortalUrl(): string {
    const pathname = location.pathname;
    const esriAppsPath = "/apps/";
    const esriHomePath = "/home/";
    const esriAppsPathIndex = pathname.indexOf(esriAppsPath);
    const esriHomePathIndex = pathname.indexOf(esriHomePath);
    const isEsriAppsPath = esriAppsPathIndex !== -1;
    const isEsriHomePath = esriHomePathIndex !== -1;
    const appLocationIndex = isEsriAppsPath
      ? esriAppsPathIndex
      : isEsriHomePath
      ? esriHomePathIndex
      : null;

    if (appLocationIndex == null) {
      return `${location.protocol}//devext.arcgis.com`;
    }

    const portalInstance = pathname.substr(0, appLocationIndex);
    const host = location.host;
    return `${location.protocol}//${host}${portalInstance}`;
  }

  private _registerOAuthInfos(): void {
    const { oauthAppId, portalUrl } = this.appConfig;
    const info = new OAuthInfo({
      appId: oauthAppId,
      portalUrl,
    });
    if (info) {
      IdentityManager.registerOAuthInfos([info]);
    }
  }

  private async init() {
    const portalAppResponse = await this._initPortal();
    const { portal } = portalAppResponse;

    this.appConfig.isReadOnly = !!portal?.sourceJSON?.isReadOnly;

    this.secondLocaleSet(portal);

    const telemetry = this._initTelemetry(portal);
    telemetry.logPageView();
    if (
      (!portal?.user?.canCreateItems ||
        !portal?.user?.userRole?.canCreateItem ||
        !portal?.user?.userRole?.canCreateItem?.()) &&
      (portal?.user?.accountType != "Public" || isEnterprise(portal))
    ) {
      this._isUserNotAllowed = true;
      return;
    }

    this.popoverManager = new PopoverManager();

    new InstantAppLanding({
      container: "instant-app-landing",
      portal,
      telemetry,
      appConfig: this.appConfig,
      popoverManager: this.popoverManager,
    });
  }

  /** 2nd time we set the locale. Includes User Profile locale in decision */
  private secondLocaleSet(portal: __esri.Portal) {
    this.appConfig.locale = decideAndSetLocale(portal);
    this.appConfig.direction = decideAndSetDir(this.appConfig.locale);
  }

  private async _initPortal(): Promise<any> {
    const { portalUrl, oauthAppId } = this.appConfig;
    const sharingUrl = `${portalUrl}/sharing`;
    const checkAppAccess = IdentityManager.checkAppAccess(
      sharingUrl,
      oauthAppId
    );
    return checkAppAccess.then(
      (result) => {
        return this._createPortal(result, portalUrl);
      },
      (error) => {
        console.error("APP ACCESS ERROR: ", error);
        return this._createPortal(error, portalUrl);
      }
    );
  }

  private async _createPortal(
    appAccessResponse: any,
    url: string
  ): Promise<any> {
    const { viewOnly } = appAccessResponse;
    const portal = new Portal({
      url,
      authMode: "immediate",
    });
    return portal
      .load()
      .then(async (loadedPortal) => {
        this._setPortalUser(loadedPortal, viewOnly);
        const authorizedMessage = await this._getMessage(appAccessResponse);
        console.warn(
          "PORTAL SUCCESS - authorize message: ",
          authorizedMessage?.name
            ? authorizedMessage?.name
            : "Authorization successful"
        );
        return {
          portal: loadedPortal,
          authorizedMessage,
        };
      })
      .catch(async (loadedErrorPortal) => {
        const authorizedMessage = await this._getMessage(appAccessResponse);
        console.error(
          "PORTAL FAIL - authorize message: ",
          authorizedMessage?.name
        );
        return {
          portal: loadedErrorPortal,
          authorizedMessage,
        };
      });
  }

  private _setPortalUser(portal: __esri.Portal, viewOnly: boolean): void {
    let user;
    const portalUser = portal.user as __esri.PortalUser;
    if (portalUser) {
      const json = portalUser && portalUser.sourceJSON;
      user = new PortalUser();
      user.sourceJSON = json;
      user.portal = portal;
      user.isViewOnly = viewOnly || false;
      user.read(json);
      user.init();
      portal.user = user;
    }
  }

  private async _getMessage(appAccessResponse: any): Promise<any> {
    return this._checkForAuthorization(appAccessResponse);
  }

  private async _checkForAuthorization(appAccessResponse: any): Promise<any> {
    const id = this._getAppId();
    if (!id) {
      return {
        name: "error:does-not-exist-or-inaccessible",
        header: this.i18nNoAppAccess?.noAppAccess?.error,
        message: this.i18nNoAppAccess?.noAppAccess?.itemDoesNotExist,
      };
    }
    const appPortalItem = new PortalItem({
      id,
      portal: {
        url: this.appConfig.portalUrl,
      },
    });
    return appPortalItem
      .load()
      .then((loadedAppPortalItem) => {
        // do we have permission to access app
        const notAuthorized = "identity-manager:not-authorized";
        const appAccessResponseName = appAccessResponse?.name;
        const appAccess = loadedAppPortalItem?.access;
        const userId = loadedAppPortalItem?.portal?.credential?.userId;
        const itemUrl = loadedAppPortalItem?.itemUrl;
        if (appAccess !== "public" && appAccessResponseName === notAuthorized) {
          // identity-manager:not-authorized, identity-manager:not-authenticated, identity-manager:invalid-request
          return {
            name: notAuthorized,
            header: this.i18nNoAppAccess.noAppAccess.notLicensed,
            message: this.i18nNoAppAccess.noAppAccess.notLicensedMessage,
          };
        } else if (appAccessResponseName === notAuthorized) {
          return {
            name: appAccessResponseName,
            header: this.i18nNoAppAccess.noAppAccess.notAuthorized,
            message: substitute(
              this.i18nNoAppAccess.noAppAccess.notAuthorizedMessage,
              {
                userId,
                itemUrl,
              }
            ),
          };
        } else if (loadedAppPortalItem?.error) {
          return loadedAppPortalItem;
        }
      })
      .catch((err) => {
        console.error("PORTAL ITEM LOAD ERROR: ", err);
        return err;
      });
  }

  private _getAppId(): string {
    const { href } = window.location;
    const url = new URL(href);
    const { searchParams } = url;
    const appidValue = searchParams.get("appid") as string;
    const appid = appidValue ? appidValue : "";
    return appid;
  }

  private _initTelemetry(portal: Portal): Telemetry {
    const { telemetry } = this.appConfig;
    // Setup Telemetry
    if (telemetry) {
      let options = telemetry.prod;
      if (portal.customBaseUrl.indexOf("mapsdevext") !== -1) {
        // use devext credentials
        options = telemetry.devext;
      } else if (portal.customBaseUrl.indexOf("mapsqa") !== -1) {
        // or qa
        options = telemetry.qaext;
      }

      return new Telemetry({
        portal,
        disabled: options.disabled,
        plugins: [new Amazon({ ...options.amazon })],
      });
    }
  }
}
